import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'

import { cn } from '@/custolink-newdesign/lib/utils'
import NavbarContent from '@/custolink-newdesign/shell/NavbarContent'
import Toolbar from '@/custolink-newdesign/shell/Toolbar'
import { Sheet, SheetContent, SheetTitle } from '@/custolink-newdesign/ui/sheet'
import { LayoutContext } from '@/utils/hooks/useLayout'
import { LAYOUT_COLLAPSIBLE_SIDE } from '@/constants/theme.constant'

/**
 * The authenticated shell. flow Layout1.tsx:33-61 + NavbarStyle1.tsx:33-66.
 *
 * Replaces components/layouts/PostLoginLayout/CollapsibleSide.tsx when
 * the new design is not switched off with ?new_design=0. Differences from the
 * old shell, all inherited from flow:
 *
 *  - The rail is 280px and always expanded or fully off, rather than the old
 *    290px/80px collapse-to-icons.
 *  - The rail scrolls with its own overflow and the page body scrolls
 *    separately; the old shell scrolls the whole document.
 *  - Below 1280px the rail becomes a Sheet. The old shell's breakpoint is
 *    1024px (useResponsive larger.lg); flow's is LG_BREAKPOINT_PX = 1280
 *    (hooks/useBreakpointDown.ts:13-15).
 */
const LG_BREAKPOINT_PX = 1280

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < LG_BREAKPOINT_PX
  )
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${LG_BREAKPOINT_PX - 0.05}px)`)
    const on = () => setIsMobile(mq.matches)
    on()
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return isMobile
}

export default function AppShell({ children }: { children: ReactNode }) {
  const isMobile = useIsMobile()
  const [navOpen, setNavOpen] = useState(true)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  return (
    // LayoutContext is REQUIRED even though nothing in the new design reads it.
    // Routes that have not been converted yet still render through
    // AdminAreaProtectedPageContainer -> PageContainer, which calls useLayout()
    // and throws "useLayout must be used within a LayoutProvider" without it.
    // The old shell supplies this via LayoutBase; caught in the browser when
    // /admin/assets crashed under ?new_design=1.
    <LayoutContext.Provider value={{ type: LAYOUT_COLLAPSIBLE_SIDE }}>
    <div
      id="app-layout"
      className="cl-app-layout cl:flex cl:w-full cl:flex-auto"
      style={{ '--app-container-width': '1120px' } as CSSProperties}
    >
      <div className="cl:flex cl:min-w-0 cl:flex-auto">
        {!isMobile && (
          <div
            className={cn(
              'cl:sticky cl:top-0 cl:z-20 cl:h-screen cl:flex-auto cl:shrink-0 cl:flex-col',
              'cl-app-navbar'
            )}
            data-open={String(navOpen)}
            data-position="left"
          >
            <NavbarContent />
          </div>
        )}

        {isMobile && (
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetContent side="left" className="cl:w-70 cl:gap-0 cl:p-0 cl:sm:max-w-70">
              <SheetTitle className="cl:sr-only">Navigation</SheetTitle>
              <NavbarContent />
            </SheetContent>
          </Sheet>
        )}

        <main
          id="app-main"
          className="cl:relative cl:z-10 cl:flex cl:min-h-svh cl:min-w-0 cl:flex-auto cl:flex-col"
        >
          <Toolbar
            className="cl:sticky cl:top-0"
            onToggleNavbar={() =>
              isMobile ? setMobileNavOpen((v) => !v) : setNavOpen((v) => !v)
            }
          />

          <div className="cl:relative cl:z-10 cl:flex cl:min-h-0 cl:flex-auto cl:flex-col">
            {children}
          </div>
        </main>
      </div>
    </div>
    </LayoutContext.Provider>
  )
}
